import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as log from "@/lib/logger";
import { Lock, Hash } from "lucide-react";
import InfoTab from "@/components/channel/settings/InfoTab";
import MembersTab from "@/components/channel/settings/MembersTab";
import DangerTab from "@/components/channel/settings/DangerTab";

/**
 * Channel settings — edit info, manage members, archive or hard-delete.
 * Access is gated server-side; UI hides destructive actions when the current
 * user cannot manage the channel to avoid confusion.
 *
 * This dialog owns state + API side-effects; the three tab bodies (`InfoTab`,
 * `MembersTab`, `DangerTab`) are presentational.
 */
export default function ChannelSettingsDialog({
  open,
  onClose,
  channel,
  onChannelUpdated,
  onChannelRemoved,
  canManage,
  currentUserId,
}) {
  const [tab, setTab] = useState("info");
  const [name, setName] = useState(channel?.name || "");
  const [description, setDescription] = useState(channel?.description || "");
  const [topic, setTopic] = useState(channel?.topic || "");
  const [priv, setPriv] = useState(!!channel?.is_private);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [confirmMode, setConfirmMode] = useState(null); // "archive" | "delete" | null
  const [confirmName, setConfirmName] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => {
    if (!open || !channel) return;
    setTab("info");
    setName(channel.name || "");
    setDescription(channel.description || "");
    setTopic(channel.topic || "");
    setPriv(!!channel.is_private);
    setConfirmMode(null);
    setConfirmName("");
    (async () => {
      try {
        const [{ data: mems }, { data: org }] = await Promise.all([
          api.get(`/channels/${channel.id}/members`),
          api.get("/orgs/current/members"),
        ]);
        setMembers(mems || []);
        setOrgMembers(org || []);
      } catch (err) { log.debug("[channel-settings] load", err); }
    })();
  }, [open, channel?.id]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);
  const addable = useMemo(
    () => orgMembers.filter((m) => !memberIds.has(m.user_id)),
    [orgMembers, memberIds]
  );

  const saveInfo = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      const payload = {};
      if (name !== channel.name) payload.name = name;
      if (description !== (channel.description || "")) payload.description = description;
      if (topic !== (channel.topic || "")) payload.topic = topic;
      if (priv !== !!channel.is_private) payload.is_private = priv;
      if (Object.keys(payload).length === 0) { toast("Nothing changed"); setSaving(false); return; }
      const { data } = await api.patch(`/channels/${channel.id}`, payload);
      toast.success("Channel updated");
      onChannelUpdated && onChannelUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update channel");
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (uid) => {
    try {
      await api.post(`/channels/${channel.id}/members`, { user_ids: [uid] });
      const { data: mems } = await api.get(`/channels/${channel.id}/members`);
      setMembers(mems || []);
      toast.success("Member added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add member");
    }
  };

  const removeMember = async (uid) => {
    try {
      await api.delete(`/channels/${channel.id}/members/${uid}`);
      const { data: mems } = await api.get(`/channels/${channel.id}/members`);
      setMembers(mems || []);
      toast.success("Member removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove member");
    }
  };

  const executeDestroy = async () => {
    if (confirmName !== channel.name) {
      toast.error("Type the channel name to confirm");
      return;
    }
    setConfirmBusy(true);
    try {
      if (confirmMode === "archive") {
        await api.post(`/channels/${channel.id}/archive`);
        toast.success(`#${channel.name} archived`);
      } else {
        await api.delete(`/channels/${channel.id}`);
        toast.success(`#${channel.name} deleted`);
      }
      onChannelRemoved && onChannelRemoved(channel.id);
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setConfirmBusy(false);
    }
  };

  if (!channel) return null;
  const isPublic = !channel.is_private;
  const tabs = ["info", "members", canManage && "danger"].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="channel-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {channel.is_private ? <Lock className="w-4 h-4"/> : <Hash className="w-4 h-4"/>}
            <span>#{channel.name}</span>
          </DialogTitle>
          <DialogDescription>Manage channel details, members, and lifecycle.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-zinc-200 -mt-2">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`channel-settings-tab-${t}`}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t
                  ? "border-[var(--brand-accent)] text-zinc-900"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {t === "info" && "Info"}
              {t === "members" && "Members"}
              {t === "danger" && <span className="text-red-600">Danger zone</span>}
            </button>
          ))}
        </div>

        {tab === "info" && (
          <InfoTab
            name={name} setName={setName}
            description={description} setDescription={setDescription}
            topic={topic} setTopic={setTopic}
            priv={priv} setPriv={setPriv}
            canManage={canManage} saving={saving} onSave={saveInfo}
          />
        )}

        {tab === "members" && (
          <MembersTab
            isPublic={isPublic}
            members={members}
            addable={addable}
            canManage={canManage}
            currentUserId={currentUserId}
            createdBy={channel.created_by}
            onAdd={addMember}
            onRemove={removeMember}
          />
        )}

        {tab === "danger" && canManage && (
          <DangerTab
            channelName={channel.name}
            confirmMode={confirmMode} setConfirmMode={setConfirmMode}
            confirmName={confirmName} setConfirmName={setConfirmName}
            confirmBusy={confirmBusy}
            onExecute={executeDestroy}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
